// The one fetch wrapper. Always sends the session cookie, speaks JSON, and
// throws ApiError with the API's error code (docs/API.md section 2).
import type { ErrorCode, FieldErrors } from "@convenor/shared";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode | "NETWORK",
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** zod flatten() details of a VALIDATION_FAILED error. */
  fieldErrors(): FieldErrors {
    const d = this.details as Partial<FieldErrors> | undefined;
    return { fieldErrors: d?.fieldErrors ?? {}, formErrors: d?.formErrors ?? [] };
  }
}

export const isApiError = (e: unknown, code?: string): e is ApiError =>
  e instanceof ApiError && (code === undefined || e.code === code);

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: "include",
      headers: body === undefined ? { Accept: "application/json" } : { Accept: "application/json", "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "NETWORK", "Could not reach the server. Check your connection and try again.");
  }
  if (res.status === 204) return undefined as T;
  const isJson = (res.headers.get("content-type") ?? "").includes("application/json");
  const data: unknown = isJson ? await res.json() : undefined;
  if (!res.ok) {
    const error = (data as { error?: { code: ErrorCode; message: string; details?: unknown } } | undefined)?.error;
    throw new ApiError(
      res.status,
      error?.code ?? "INTERNAL",
      error?.message ?? "Something went wrong on our side. Please try again.",
      error?.details,
    );
  }
  return data as T;
}

/** GET a binary file. Errors are parsed like any other call. */
async function download(path: string, method: "GET" | "POST" = "GET"): Promise<{ blob: Blob; filename: string | null }> {
  let res: Response;
  try {
    res = await fetch(path, { method, credentials: "include" });
  } catch {
    throw new ApiError(0, "NETWORK", "Could not reach the server. Check your connection and try again.");
  }
  if (!res.ok) {
    const isJson = (res.headers.get("content-type") ?? "").includes("application/json");
    const data = isJson ? ((await res.json()) as { error?: { code: ErrorCode; message: string } }) : undefined;
    throw new ApiError(
      res.status,
      data?.error?.code ?? "INTERNAL",
      data?.error?.message ?? "The file could not be downloaded. Please try again.",
    );
  }
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return { blob: await res.blob(), filename: match ? match[1] : null };
}

export const api = {
  download,
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown = {}) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  delete: <T = void>(path: string) => request<T>("DELETE", path),
};

/** Builds "?a=1&b=2" from defined, non-empty values. */
export function queryString(params: Record<string, string | number | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : "";
}
